/** Central enums for the PipeMitra Admin prototype. Never inline these strings. */

export const ProjectStatus = {
  ReadyToMeasure: 'READY_TO_MEASURE',
  DistanceMeasured: 'DISTANCE_MEASURED',
  HeadCalculationInProgress: 'HEAD_CALCULATION_IN_PROGRESS',
  ReportGenerated: 'REPORT_GENERATED',
} as const;
export type ProjectStatus = (typeof ProjectStatus)[keyof typeof ProjectStatus];

export const ReportAvailability = {
  InReview: 'IN_REVIEW',
  Available: 'AVAILABLE',
} as const;
export type ReportAvailability = (typeof ReportAvailability)[keyof typeof ReportAvailability];

/** Report generation lifecycle — API `reportStatus` (ticket 101). */
export const ReportStatus = {
  AwaitingPayment: 'AWAITING_PAYMENT',
  Generating: 'GENERATING',
  Generated: 'GENERATED',
  Failed: 'FAILED',
} as const;
export type ReportStatus = (typeof ReportStatus)[keyof typeof ReportStatus];

export const PaymentStatus = {
  Created: 'CREATED',
  Pending: 'PENDING',
  Success: 'SUCCESS',
  Failed: 'FAILED',
  Cancelled: 'CANCELLED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const UserType = {
  Individual: 'INDIVIDUAL',
  Business: 'BUSINESS',
  Technician: 'TECHNICIAN',
} as const;
export type UserType = (typeof UserType)[keyof typeof UserType];

export const SupportedLanguage = {
  English: 'en',
  Hindi: 'hi',
  Marathi: 'mr',
} as const;
export type SupportedLanguage = (typeof SupportedLanguage)[keyof typeof SupportedLanguage];

export const AccountStatus = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
} as const;
export type AccountStatus = (typeof AccountStatus)[keyof typeof AccountStatus];

export const InboxEventType = {
  UserRegistered: 'USER_REGISTERED',
  ReportPaymentSuccess: 'REPORT_PAYMENT_SUCCESS',
  InvoiceGenerated: 'INVOICE_GENERATED',
  ExportReady: 'EXPORT_READY',
  SystemWarning: 'SYSTEM_WARNING',
} as const;
export type InboxEventType = (typeof InboxEventType)[keyof typeof InboxEventType];

export const PipeMaterial = { Pvc: 'PVC', Hdpe: 'HDPE' } as const;
export type PipeMaterial = (typeof PipeMaterial)[keyof typeof PipeMaterial];

/** Solution-wide canonical Project Type. No other value is permitted. */
export const ProjectType = {
  Distance: 'DISTANCE',
  HeadCalculation: 'HEAD_CALCULATION',
} as const;
export type ProjectType = (typeof ProjectType)[keyof typeof ProjectType];

export const RouteMethod = {
  DrawOnMap: 'DRAW_ON_MAP',
  MeasureOnSite: 'MEASURE_ON_SITE',
} as const;
export type RouteMethod = (typeof RouteMethod)[keyof typeof RouteMethod];

export const PredictionTrend = {
  Rising: 'RISING',
  Stable: 'STABLE',
  Decreasing: 'DECREASING',
} as const;
export type PredictionTrend = (typeof PredictionTrend)[keyof typeof PredictionTrend];

/** V1 has exactly one approved Admin role. */
export type AdminRole = 'SUPER_ADMIN';

export const Capability = {
  UsersView: 'users.view',
  UsersManage: 'users.manage',
  ProjectsView: 'projects.view',
  InvoicesView: 'invoices.view',
  NotificationsManage: 'notifications.manage',
  ContentManage: 'content.manage',
  SettingsManage: 'settings.manage',
  PrototypeManage: 'prototype.manage',
} as const;
export type Capability = (typeof Capability)[keyof typeof Capability];

export const ALL_CAPABILITIES: readonly Capability[] = Object.values(Capability);
/** Publication state of a fixed Pipe Rate Prediction record. */
export const PredictionStatus = {
  NeedsSetup: 'NEEDS_SETUP',
  Published: 'PUBLISHED',
} as const;
export type PredictionStatus = (typeof PredictionStatus)[keyof typeof PredictionStatus];

/** Languages a prediction must be authored in before it can be published. */
export const PredictionLanguage = {
  English: 'en',
  Hindi: 'hi',
  Marathi: 'mr',
} as const;
export type PredictionLanguage = (typeof PredictionLanguage)[keyof typeof PredictionLanguage];

/** A Banner belongs to exactly one placement; ordering is per placement. */
export const BannerPlacement = { Home: 'HOME', Login: 'LOGIN' } as const;
export type BannerPlacement = (typeof BannerPlacement)[keyof typeof BannerPlacement];

/** Concurrent active banners allowed per placement. */
export const BANNER_PLACEMENT_CAPACITY: Record<BannerPlacement, number> = {
  HOME: 3,
  LOGIN: 1,
};

/** Largest pixel size the Home / Login drop-zone accepts. Smaller images are allowed. */
export const BANNER_CREATIVE_PIXELS: Record<BannerPlacement, { width: number; height: number }> = {
  HOME: { width: 1029, height: 516 },
  LOGIN: { width: 1124, height: 810 },
};

/** Derived from the schedule and the scenario reference date. Never editable. */
export const LifecycleStatus = {
  Active: 'ACTIVE',
  Scheduled: 'SCHEDULED',
  Expired: 'EXPIRED',
} as const;
export type LifecycleStatus = (typeof LifecycleStatus)[keyof typeof LifecycleStatus];

/** Where an advertisement creative is shown. Drives the Add Advertisement type tabs. */
export const AdvertisementType = {
  App: 'APP',
  Report: 'REPORT',
} as const;
export type AdvertisementType = (typeof AdvertisementType)[keyof typeof AdvertisementType];

export const ADVERTISEMENT_TYPE_ORDER: readonly AdvertisementType[] = [
  AdvertisementType.App,
  AdvertisementType.Report,
];

export const ADVERTISEMENT_TYPE_LABELS: Record<AdvertisementType, string> = {
  APP: 'App',
  REPORT: 'Report',
};

/** Largest pixel size the Advertisement drop-zone accepts, per type. Smaller images are allowed. */
export const ADVERTISEMENT_CREATIVE_PIXELS: Record<
  AdvertisementType,
  { width: number; height: number }
> = {
  APP: { width: 1124, height: 150 },
  REPORT: { width: 1617, height: 270 },
};

/**
 * Approved Advertisement display targets for the App type.
 * Includes every value the backend registry and mobile still accept — including
 * `REPORTS`, which the admin picker no longer offers (see `ADVERTISEMENT_PAGE_ORDER`).
 * Keeping it here lets existing ads stay visible and survive an edit round trip.
 */
export const AdvertisementPage = {
  Profile: 'PROFILE',
  Notification: 'NOTIFICATION',
  TermsAndConditions: 'TERMS_AND_CONDITIONS',
  PrivacyPolicy: 'PRIVACY_POLICY',
  HelpTutorials: 'HELP_TUTORIALS',
  LanguageSelection: 'LANGUAGE_SELECTION',
  Projects: 'PROJECTS',
  ProjectDetails: 'PROJECT_DETAILS',
  MeasureDistance: 'MEASURE_DISTANCE',
  PipeRatePrediction: 'PIPE_RATE_PREDICTION',
  /** Still valid on the wire and mobile; omitted from the admin picker only. */
  Reports: 'REPORTS',
} as const;
export type AdvertisementPage = (typeof AdvertisementPage)[keyof typeof AdvertisementPage];

/** Pages the admin Display Pages picker offers for new selections. */
export const ADVERTISEMENT_PAGE_ORDER: readonly AdvertisementPage[] = [
  AdvertisementPage.Profile,
  AdvertisementPage.Notification,
  AdvertisementPage.TermsAndConditions,
  AdvertisementPage.PrivacyPolicy,
  AdvertisementPage.HelpTutorials,
  AdvertisementPage.LanguageSelection,
  AdvertisementPage.Projects,
  AdvertisementPage.ProjectDetails,
  AdvertisementPage.MeasureDistance,
  AdvertisementPage.PipeRatePrediction,
];

export const ADVERTISEMENT_PAGE_LABELS: Record<AdvertisementPage, string> = {
  PROFILE: 'Profile',
  NOTIFICATION: 'Notification',
  TERMS_AND_CONDITIONS: 'Terms and Conditions',
  PRIVACY_POLICY: 'Privacy Policy',
  HELP_TUTORIALS: 'Help & Tutorials',
  LANGUAGE_SELECTION: 'Language selection',
  PROJECTS: 'Projects',
  PROJECT_DETAILS: 'Project Details',
  MEASURE_DISTANCE: 'Measure Distance',
  PIPE_RATE_PREDICTION: 'Pipe Rate Prediction',
  REPORTS: 'Reports',
};

/** Lifecycle of a broadcast notification. Never inferred from dates alone. */
export const NotificationStatus = {
  Draft: 'DRAFT',
  Scheduled: 'SCHEDULED',
  Sent: 'SENT',
  Failed: 'FAILED',
} as const;
export type NotificationStatus = (typeof NotificationStatus)[keyof typeof NotificationStatus];

export const NOTIFICATION_STATUS_ORDER: readonly NotificationStatus[] = [
  NotificationStatus.Draft,
  NotificationStatus.Scheduled,
  NotificationStatus.Sent,
  NotificationStatus.Failed,
];

export const NOTIFICATION_STATUS_LABELS: Record<NotificationStatus, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  SENT: 'Sent',
  FAILED: 'Failed',
};

/** The only approved broadcast audiences. Subscribed Users does not exist. */
export const NotificationAudience = {
  AllUsers: 'ALL_USERS',
  ReportPurchasers: 'REPORT_PURCHASERS',
} as const;
export type NotificationAudience = (typeof NotificationAudience)[keyof typeof NotificationAudience];

export const NOTIFICATION_AUDIENCE_LABELS: Record<NotificationAudience, string> = {
  ALL_USERS: 'All Users',
  REPORT_PURCHASERS: 'Report Purchasers',
};
